import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw, Users, Mail, MousePointer, UserMinus, ExternalLink, Activity, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrowthChart = ({ snapshots }) => {
  if (!snapshots || snapshots.length === 0) {
    return (
      <div className="bg-white border-2 border-dashed border-[#CCFBF1] rounded-2xl p-10 text-center" data-testid="chart-empty">
        <TrendingUp className="w-10 h-10 text-[#25a1c2]/30 mx-auto mb-3" />
        <p className="font-serif text-lg text-[#475569]">Growth chart fills in over time</p>
        <p className="text-xs text-[#475569] mt-1">First snapshot saved today. Come back tomorrow for the curve.</p>
      </div>
    );
  }
  const W = 800, H = 200, P = 40;
  const max = Math.max(...snapshots.map((s) => s.total_active || 0), 5);
  const stepX = snapshots.length > 1 ? (W - P * 2) / (snapshots.length - 1) : 0;
  const points = snapshots.map((s, i) => {
    const x = P + i * stepX;
    const y = H - P - ((s.total_active || 0) / max) * (H - P * 2);
    return { x, y, val: s.total_active, date: s.date };
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${path} L ${points[points.length - 1].x} ${H - P} L ${points[0].x} ${H - P} Z`;
  const latest = snapshots[snapshots.length - 1];
  const earliest = snapshots[0];
  const delta = (latest.total_active || 0) - (earliest.total_active || 0);

  return (
    <div className="bg-white border border-[#CCFBF1] rounded-2xl p-5 md:p-6" data-testid="growth-chart">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#25a1c2]">Active Subscribers Over Time</p>
          <h3 className="font-serif text-xl text-[#042F2E]">Growth · {snapshots.length} day{snapshots.length === 1 ? "" : "s"}</h3>
        </div>
        <div className="text-right">
          <p className={`font-serif text-2xl font-bold ${delta >= 0 ? "text-[#25a1c2]" : "text-amber-600"}`}>
            {delta >= 0 ? "+" : ""}{delta}
          </p>
          <p className="text-[10px] uppercase tracking-widest text-[#475569] font-bold">since start</p>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <defs>
          <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#25a1c2" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#25a1c2" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#grad)" />
        <path d={path} fill="none" stroke="#25a1c2" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="5" fill="#25a1c2" />
            <circle cx={p.x} cy={p.y} r="3" fill="#fff" />
          </g>
        ))}
        <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#CCFBF1" strokeWidth="1" />
      </svg>
      <div className="flex justify-between text-[10px] text-[#475569] font-mono mt-2 px-1">
        <span>{earliest.date}</span>
        <span className="text-[#25a1c2] font-bold">latest: {latest.total_active} active</span>
        <span>{latest.date}</span>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className="bg-white border border-[#CCFBF1] rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#475569]">{label}</p>
    </div>
    <p className="font-serif text-3xl text-[#042F2E] font-bold">{value.toLocaleString()}</p>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function FunnelStats() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, histRes] = await Promise.all([
        axios.get(`${API}/funnel-stats`),
        axios.get(`${API}/funnel-stats/history`),
      ]);
      if (statsRes.data.success) {
        setData(statsRes.data);
      } else {
        setError(statsRes.data.error || "Could not load stats");
      }
      if (histRes.data.success) {
        setHistory(histRes.data.snapshots || []);
      }
    } catch (e) {
      setError("Network error — try refreshing");
    } finally {
      setLoading(false);
      setLastRefresh(Date.now());
    }
  };

  useEffect(() => {
    document.title = "Funnel Stats · Live MailerLite Dashboard | Temple Franklin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    fetchStats();
    return () => document.head.removeChild(meta);
  }, []);

  const totalActive = data?.groups?.reduce((sum, g) => sum + (g.active_count || 0), 0) || 0;
  const totalUnsub = data?.groups?.reduce((sum, g) => sum + (g.unsubscribed_count || 0), 0) || 0;
  const totalOpens = data?.groups?.reduce((sum, g) => sum + (g.opens_count || 0), 0) || 0;
  const totalClicks = data?.groups?.reduce((sum, g) => sum + (g.clicks_count || 0), 0) || 0;

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="funnel-stats-page">
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/social" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Publish Toolkit
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Live · MailerLite
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-6xl mx-auto">
          {/* HERO */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#25a1c2] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
              <Activity className="w-3 h-3" /> Real-time numbers
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-3">
              Your Funnel · Live
            </h1>
            <p className="text-[#475569] max-w-xl mx-auto">
              Pulled directly from MailerLite. Refreshes when you tap the button. No external dashboard required.
            </p>
          </div>

          {/* TOTALS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="totals-grid">
            <StatCard icon={Users} label="Active Subscribers" value={totalActive} color="text-[#25a1c2]" testId="stat-active" />
            <StatCard icon={Mail} label="Total Opens" value={totalOpens} color="text-[#25a1c2]" testId="stat-opens" />
            <StatCard icon={MousePointer} label="Total Clicks" value={totalClicks} color="text-[#25a1c2]" testId="stat-clicks" />
            <StatCard icon={UserMinus} label="Unsubscribes" value={totalUnsub} color="text-amber-600" testId="stat-unsub" />
          </div>

          {/* GROWTH CHART */}
          <div className="mb-10">
            <GrowthChart snapshots={history} />
          </div>

          {/* GROUPS */}
          <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-serif text-2xl text-[#042F2E]">Groups · per-funnel breakdown</h2>
            <button
              onClick={fetchStats}
              disabled={loading}
              className="inline-flex items-center gap-1.5 bg-[#25a1c2] hover:bg-[#068fa2] disabled:opacity-50 text-white text-xs font-bold rounded-full px-4 py-2 transition-all"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-8 text-red-900 text-sm" data-testid="error">
              <p className="font-bold">Error: {error}</p>
            </div>
          )}

          {!error && data && (
            <div className="space-y-3" data-testid="groups-list">
              {data.groups.length === 0 ? (
                <div className="bg-white border border-[#CCFBF1] rounded-2xl p-8 text-center text-[#475569]">
                  <p>No groups yet. Create one in MailerLite to see it here.</p>
                </div>
              ) : (
                data.groups.map((g) => (
                  <div
                    key={g.id}
                    className="bg-white border border-[#CCFBF1] rounded-2xl p-5 md:p-6 hover:border-[#25a1c2] transition-all"
                    data-testid={`group-${g.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                      <div>
                        <h3 className="font-serif text-xl text-[#042F2E] font-bold">{g.name}</h3>
                        <p className="text-[10px] text-[#475569] font-mono">ID: {g.id} · created {formatDate(g.created_at)}</p>
                      </div>
                      <a
                        href={`https://dashboard.mailerlite.com/subscribers?group=${g.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#25a1c2] hover:text-[#042F2E] text-xs font-bold"
                      >
                        Open in MailerLite <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="bg-[#F0FDF4] rounded-lg p-3 border border-[#25a1c2]/20">
                        <p className="text-[10px] uppercase tracking-widest text-[#25a1c2] font-bold">Active</p>
                        <p className="font-serif text-2xl text-[#042F2E]">{g.active_count.toLocaleString()}</p>
                      </div>
                      <div className="bg-[#efeae7] rounded-lg p-3 border border-[#CCFBF1]">
                        <p className="text-[10px] uppercase tracking-widest text-[#475569] font-bold">Sent</p>
                        <p className="font-serif text-2xl text-[#042F2E]">{g.sent_count.toLocaleString()}</p>
                      </div>
                      <div className="bg-[#efeae7] rounded-lg p-3 border border-[#CCFBF1]">
                        <p className="text-[10px] uppercase tracking-widest text-[#475569] font-bold">Opens</p>
                        <p className="font-serif text-2xl text-[#042F2E]">{g.opens_count.toLocaleString()}</p>
                      </div>
                      <div className="bg-[#efeae7] rounded-lg p-3 border border-[#CCFBF1]">
                        <p className="text-[10px] uppercase tracking-widest text-[#475569] font-bold">Clicks</p>
                        <p className="font-serif text-2xl text-[#042F2E]">{g.clicks_count.toLocaleString()}</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                        <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Unsubs</p>
                        <p className="font-serif text-2xl text-amber-900">{g.unsubscribed_count.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {loading && !data && (
            <div className="bg-white border border-[#CCFBF1] rounded-2xl p-12 text-center text-[#475569]">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-[#25a1c2]" />
              <p>Loading live numbers from MailerLite…</p>
            </div>
          )}

          <p className="text-center text-[10px] text-[#475569] italic mt-8">
            Last refreshed: {new Date(lastRefresh).toLocaleString()} · Bookmark: mindbodyspirithygienetools.com/funnel-stats
          </p>
        </div>
      </div>
    </div>
  );
}
